import { getFileExt } from '../types'
import type { GccTaxonomyRead } from './types'

/**
 * GCC Client Taxonomy upload rules (WI 2332). Shared by the Step 1 picker and
 * the POST /api/ai/gcc schema so a file the UI turns away is also refused by
 * the server, instead of slipping through and failing as a generic 500.
 */
export const GCC_TAXONOMY_EXTS = ['.pdf', '.docx', '.pptx', '.xlsx'] as const

/** Matches the "max 20 MB" hint shown under the picker. */
export const GCC_TAXONOMY_MAX_BYTES = 20 * 1024 * 1024

const ACCEPTED_LABEL = GCC_TAXONOMY_EXTS.map((e) => e.slice(1).toUpperCase()).join(', ')

/** True when the extension (case-insensitive) is one the GCC pipeline can parse. */
export function isGccTaxonomyFile(name: string): boolean {
  return (GCC_TAXONOMY_EXTS as readonly string[]).includes(getFileExt(name))
}

export function gccUnsupportedTypeMessage(name: string): string {
  return `"${name}" is not a supported file type. Accepted: ${ACCEPTED_LABEL}.`
}

/**
 * Decoded byte length of a base64 string, without decoding it. Lets the server
 * apply the 20 MB limit to the base64 uploads it receives (WI 2364).
 */
export function base64DecodedBytes(b64: string): number {
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0
  return Math.max(0, Math.floor((b64.length * 3) / 4) - padding)
}

/**
 * Extracted text at or below this many characters (after trimming) counts as an
 * empty document (WI 2363). Same threshold Inside-Out uses in extractCombinedText.
 */
export const GCC_TAXONOMY_MIN_TEXT_CHARS = 20

export function gccEmptyFileMessage(name: string): string {
  return `"${name}" is empty.`
}

export function gccNoReadableTextMessage(name: string): string {
  return `"${name}" contains no readable text. Upload a client taxonomy document with content.`
}

/** True when extracted text is too short to ground the task register. */
export function isGccTaxonomyTextEmpty(text: string | null | undefined): boolean {
  return (text ?? '').trim().length <= GCC_TAXONOMY_MIN_TEXT_CHARS
}

/**
 * Most taxonomy text one Step 0 prompt holds (WI 2888). Up to this length Step 0
 * makes one call; longer text is split into parts of at most this many
 * characters, one call each, merged in code (WI 2935).
 */
export const GCC_TAXONOMY_MAX_PROMPT_CHARS = 80_000

/**
 * Step 0 reads this many characters of the combined taxonomy text and leaves out
 * the rest (WI 2935; 80,000 under WI 2888). Step 1 and the calculation trace say
 * so. Same as Inside-Out's per-call budget, about 19,000 rows in the Excel guide's layout.
 */
export const GCC_TAXONOMY_MAX_READ_CHARS = 1_500_000

/** Joins the text of each uploaded file into the combined taxonomy text. */
export const GCC_TAXONOMY_FILE_SEPARATOR = '\n\n'

/**
 * How much of the combined text of these files (in upload order, joined with
 * GCC_TAXONOMY_FILE_SEPARATOR) fits within the read limit, overall and per file.
 */
export function gccTaxonomyRead(
  files: Array<{ name: string; chars: number }>,
  limitChars: number = GCC_TAXONOMY_MAX_READ_CHARS,
): GccTaxonomyRead {
  let offset = 0
  const read = files.map((f, i) => {
    if (i > 0) offset += GCC_TAXONOMY_FILE_SEPARATOR.length
    const readChars = Math.min(f.chars, Math.max(0, limitChars - offset))
    offset += f.chars
    return { name: f.name, chars: f.chars, readChars }
  })
  return { totalChars: offset, readChars: Math.min(offset, limitChars), limitChars, files: read }
}

const fmtChars = (n: number) => n.toLocaleString('en-US')

/** The files the limit cut into or left out entirely, e.g. "the end of b.xlsx; all of c.pdf". */
function leftOutFiles(read: GccTaxonomyRead): string {
  return read.files
    .filter((f) => f.readChars < f.chars)
    .map((f) => (f.readChars > 0
      ? `the end of ${f.name} (${fmtChars(f.readChars)} of ${fmtChars(f.chars)} characters read)`
      : `all of ${f.name}`))
    .join('; ')
}

/** True when part of the taxonomy text is left out of the prompt. */
function isGccTaxonomyTruncated(read: GccTaxonomyRead): boolean {
  return read.totalChars > read.readChars
}

/**
 * What Step 1 tells the user when the taxonomy is over the limit, or null when
 * all of it is read (1,500,000 characters or fewer).
 */
export function gccTaxonomyTruncationMessage(read: GccTaxonomyRead): string | null {
  if (!isGccTaxonomyTruncated(read)) return null
  const files = read.files.length
  const left = read.totalChars - read.readChars
  return `The taxonomy text is ${fmtChars(read.totalChars)} characters long${files > 1 ? ` across ${files} files` : ''}. `
    + `Only the first ${fmtChars(read.readChars)} are read; the last ${fmtChars(left)} ${left === 1 ? 'is' : 'are'} left out`
    + `${files > 1 ? `: ${leftOutFiles(read)}` : ''}. `
    + 'To include them, shorten the files or upload fewer.'
}

/** The calculation trace's one-line account of the taxonomy text read. */
export function gccTaxonomyReadSummary(read: GccTaxonomyRead): string {
  const files = `${read.files.length} file${read.files.length !== 1 ? 's' : ''}`
  if (!isGccTaxonomyTruncated(read)) return `${fmtChars(read.totalChars)} characters from ${files}, all read`
  return `${fmtChars(read.readChars)} of ${fmtChars(read.totalChars)} characters from ${files} `
    + `(limit ${fmtChars(read.limitChars)}); ${fmtChars(read.totalChars - read.readChars)} left out: ${leftOutFiles(read)}`
}

/**
 * Why a taxonomy upload would be rejected, or null when it is acceptable.
 * Size is optional so a name-only check stays possible.
 */
export function gccTaxonomyRejection(name: string, sizeBytes?: number): string | null {
  if (!isGccTaxonomyFile(name)) return gccUnsupportedTypeMessage(name)
  if (sizeBytes === 0) return gccEmptyFileMessage(name)
  if (sizeBytes !== undefined && sizeBytes > GCC_TAXONOMY_MAX_BYTES) {
    return `"${name}" is larger than the 20 MB limit.`
  }
  return null
}
